import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', type: 'project' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      await axios.post(`${API}/categories`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Categoría creada');
      fetchCategories();
      setShowModal(false);
      setFormData({ name: '', type: 'project' });
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Error al crear categoría');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al eliminar categoría');
    }
  };

  const projectCategories = categories.filter((c) => c.type === 'project');
  const blogCategories = categories.filter((c) => c.type === 'blog');

  return (
    <div data-testid="admin-categories-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Categorías</h1>
          <p className="text-sm font-sans tracking-widest uppercase text-ink/60">Gestionar Categorías</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-ink text-paper hover:bg-editorial" data-testid="add-category-button">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <p className="text-lg font-light text-ink/60">Cargando categorías...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold tracking-tight mb-4">Proyectos</h2>
            {projectCategories.length === 0 ? (
              <p className="text-ink/60">No hay categorías de proyectos</p>
            ) : (
              <div className="space-y-2">
                {projectCategories.map((category) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-paper editorial-border p-4 flex justify-between items-center"
                    data-testid="category-item"
                  >
                    <span className="text-lg">{category.name}</span>
                    <Button onClick={() => handleDelete(category.id)} variant="destructive" size="sm" data-testid="delete-category-button">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="text-2xl font-bold tracking-tight mb-4">Blog</h2>
            {blogCategories.length === 0 ? (
              <p className="text-ink/60">No hay categorías de blog</p>
            ) : (
              <div className="space-y-2">
                {blogCategories.map((category) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-paper editorial-border p-4 flex justify-between items-center"
                  >
                    <span className="text-lg">{category.name}</span>
                    <Button onClick={() => handleDelete(category.id)} variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-6" data-testid="category-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-paper p-8 max-w-md w-full editorial-border"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold tracking-tight">Nueva Categoría</h2>
              <button onClick={() => setShowModal(false)} className="hover:text-editorial transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="category-name-input"
                />
              </div>

              <div>
                <Label htmlFor="type">Tipo</Label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-ink/20 focus:border-editorial focus:outline-none"
                  data-testid="category-type-select"
                >
                  <option value="project">Proyecto</option>
                  <option value="blog">Blog</option>
                </select>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="flex-1 bg-ink text-paper hover:bg-editorial" data-testid="save-category-button">
                  Crear Categoría
                </Button>
                <Button type="button" onClick={() => setShowModal(false)} variant="outline">
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;